/**
 * ChainOfThought
 *
 * Structured reasoning-step display for AI tool and search progress.
 *
 * Primary responsibilities:
 * - Render collapsible step lists with clear status states
 * - Provide slots for search result chips and image evidence
 *
 * Dependencies:
 * - Radix Collapsible
 * - lucide-react
 */

'use client'

import {
  createContext,
  type ComponentProps,
  type ReactNode,
  useContext,
  useMemo
} from 'react'
import type { LucideIcon } from 'lucide-react'
import { Brain, ChevronDown, Circle } from 'lucide-react'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components/ui/collapsible'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { useControllableState } from '@/components/ai-elements/use-controllable-state'

interface ChainOfThoughtContextValue {
  isOpen: boolean
  setIsOpen: (open: boolean) => void
}

const ChainOfThoughtContext = createContext<ChainOfThoughtContextValue | null>(null)

function useChainOfThought() {
  const context = useContext(ChainOfThoughtContext)

  if (!context) {
    throw new Error('ChainOfThought components must be used within ChainOfThought')
  }

  return context
}

export interface ChainOfThoughtProps extends ComponentProps<typeof Collapsible> {
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
}

export function ChainOfThought({
  className,
  open,
  defaultOpen = false,
  onOpenChange,
  children,
  ...props
}: ChainOfThoughtProps) {
  const [isOpen, setIsOpen] = useControllableState({
    prop: open,
    defaultProp: defaultOpen,
    onChange: onOpenChange
  })

  const contextValue = useMemo(
    () => ({
      isOpen,
      setIsOpen
    }),
    [isOpen, setIsOpen]
  )

  return (
    <ChainOfThoughtContext.Provider value={contextValue}>
      <Collapsible
        className={cn('not-prose group/chain-of-thought w-full space-y-2', className)}
        open={isOpen}
        onOpenChange={setIsOpen}
        {...props}
      >
        {children}
      </Collapsible>
    </ChainOfThoughtContext.Provider>
  )
}

export interface ChainOfThoughtHeaderProps
  extends ComponentProps<typeof CollapsibleTrigger> {
  children?: ReactNode
}

export function ChainOfThoughtHeader({
  className,
  children,
  ...props
}: ChainOfThoughtHeaderProps) {
  useChainOfThought()

  return (
    <CollapsibleTrigger
      className={cn(
        'text-muted-foreground hover:text-foreground focus-visible:ring-ring flex w-full items-center gap-2 text-left text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2',
        className
      )}
      {...props}
    >
      <Brain className="size-3.5 shrink-0" />
      <span className="min-w-0 flex-1 truncate">{children ?? 'Chain of Thought'}</span>
      <ChevronDown className="size-3.5 shrink-0 transition-transform group-data-[state=open]/chain-of-thought:rotate-180" />
    </CollapsibleTrigger>
  )
}

export interface ChainOfThoughtContentProps
  extends ComponentProps<typeof CollapsibleContent> {}

export function ChainOfThoughtContent({
  className,
  ...props
}: ChainOfThoughtContentProps) {
  useChainOfThought()

  return (
    <CollapsibleContent
      className={cn(
        'data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down overflow-hidden pl-6 pr-2',
        className
      )}
      {...props}
    />
  )
}

export interface ChainOfThoughtStepProps extends ComponentProps<'div'> {
  icon?: LucideIcon
  label: string
  description?: string
  status?: 'complete' | 'active' | 'pending'
}

export function ChainOfThoughtStep({
  icon: Icon = Circle,
  label,
  description,
  status = 'complete',
  className,
  children,
  ...props
}: ChainOfThoughtStepProps) {
  return (
    <div
      className={cn(
        'flex gap-2 py-1.5 text-sm',
        status === 'complete' && 'text-muted-foreground',
        status === 'active' && 'text-muted-foreground',
        status === 'pending' && 'text-muted-foreground/60',
        className
      )}
      {...props}
    >
      <span
        className={cn(
          'mt-0.5 inline-flex size-4 shrink-0 items-center justify-center',
          status === 'complete' && 'text-emerald-600 dark:text-emerald-400',
          status === 'active' && 'text-primary',
          status === 'pending' && 'text-muted-foreground/50'
        )}
      >
        {status === 'pending' ? <Circle className="size-2.5" /> : <Icon className="size-3.5" />}
      </span>
      <span className="min-w-0 flex-1 space-y-2">
        <span className="block font-medium">{label}</span>
        {description && (
          <span className="text-muted-foreground block text-xs">{description}</span>
        )}
        {children}
      </span>
    </div>
  )
}

export function ChainOfThoughtSearchResults({
  className,
  ...props
}: ComponentProps<'div'>) {
  return <div className={cn('mt-2 flex flex-wrap gap-1.5', className)} {...props} />
}

export function ChainOfThoughtSearchResult({
  className,
  ...props
}: ComponentProps<typeof Badge>) {
  return (
    <Badge
      className={cn('border-border/70 bg-background text-[10px] font-normal', className)}
      variant="outline"
      {...props}
    />
  )
}

export interface ChainOfThoughtImageProps extends ComponentProps<'div'> {
  caption?: string
}

export function ChainOfThoughtImage({
  caption,
  className,
  children,
  ...props
}: ChainOfThoughtImageProps) {
  return (
    <div className={cn('border-border/70 mt-2 overflow-hidden rounded-md border', className)} {...props}>
      {children}
      {caption && (
        <div className="border-border/60 bg-muted/30 text-muted-foreground border-t px-2 py-1 text-[10px]">
          {caption}
        </div>
      )}
    </div>
  )
}
